import { useMemo } from 'react'
import { LineCurve3, Vector3 } from 'three'
import { BasicMesh } from '@modugen/ui-toolkit'
import { useAppStore } from '@stores'
import { theme } from 'src/styles'
import { getContrastTextColor } from 'src/utils/color'
import Arrow from '../Arrow'
import { loadSceneLabelsEqual, toLoadSceneTextLines } from '../LoadMesh/utils'
import ShapeMesh from '../ShapeMesh'
import Text3DThreeDoubleSided from '../Text3DThreeDoubleSided'
import { getLoadCategoryColor, highlightLoadColor } from '../loads'
import { createMeshStandardMaterialMemoized, makeXDirectionPositive } from '../utils'
import { inverseValue } from './utils'

const LINE_LOAD_TUBE_DIAMETER = 0.015
const LINE_LOAD_ARROW_HEAD_DIAMETER = LINE_LOAD_TUBE_DIAMETER + 0.005
const LINE_LOAD_ARROW_SHAFT_DIAMETER = LINE_LOAD_TUBE_DIAMETER
const LINE_LOAD_TEXT_OFFSET_HALF_WIDTH = LINE_LOAD_ARROW_SHAFT_DIAMETER / 2 + 0.001

interface Props {
  loadDisplayData: LineLoadDisplayData
  load: LineLoad
  onClick?: () => void
  isActive?: boolean
  color?: string
  opacity?: number
}

const LineLoadMeshElement = ({
  load,
  loadDisplayData,
  onClick,
  isActive = false,
  color,
  opacity = 1,
}: Props) => {
  const { magnitudePoints: loadPoint, shapeObject, label: startLabel, endLabel } = loadDisplayData

  const rawDirectionStart = useMemo(
    () => loadPoint[0].from.clone().sub(loadPoint[0].to),
    [loadPoint],
  )
  const rawDirectionEnd = useMemo(() => loadPoint[1].from.clone().sub(loadPoint[1].to), [loadPoint])

  const directionStart = useMemo(
    () => rawDirectionStart.clone().normalize().setZ(-1),
    [rawDirectionStart],
  )
  const directionEnd = useMemo(
    () => rawDirectionEnd.clone().normalize().setZ(-1),
    [rawDirectionEnd],
  )

  // Create a line curve from start to end point
  const lineCurve = useMemo(() => {
    return new LineCurve3(loadPoint[0].to, loadPoint[1].to)
  }, [loadPoint])

  const loadTextScale = useAppStore(state => state.loadRenderConfig.lineLoads.textScale)

  const loadColor = getLoadCategoryColor(load.load_case.category)
  const backgroundColor = isActive ? color || highlightLoadColor : color || loadColor
  const textColor = getContrastTextColor(backgroundColor)

  const renderColor = textColor
  const shapeOpacity = (isActive ? 0.25 : 0.2) * opacity

  // Create material for the tube
  const tubeMaterial = useMemo(() => {
    return createMeshStandardMaterialMemoized({
      color: backgroundColor || theme.palette.grey[800],
      polygonOffset: true,
      polygonOffsetUnits: -100 + 1 * 10,
      opacity,
      transparent: opacity < 1,
    })
  }, [backgroundColor, opacity])

  const isConstantLoad = loadSceneLabelsEqual(startLabel, endLabel)

  const centerTo = useMemo(
    () => loadPoint[0].to.clone().add(loadPoint[1].to.clone()).multiplyScalar(0.5),
    [loadPoint],
  )

  const offsetUnits = useMemo(() => {
    const magnitude = -load.end.z

    // asumes load is between 1 and 100 kN
    return inverseValue(magnitude, 0, 100000, 1, 10000)
  }, [load.end.z])

  const textXDirection = useMemo(() => {
    const tempX = loadPoint[0].from.clone().sub(loadPoint[1].from).normalize()
    return makeXDirectionPositive(tempX)
  }, [loadPoint])
  const textYDirection = useMemo(() => {
    const tempY = new Vector3(0, 0, 1)
    const tempZ = textXDirection.clone().cross(tempY).normalize()
    const finalY = tempZ.clone().cross(textXDirection).normalize()
    return finalY
  }, [textXDirection])

  return (
    <BasicMesh cursor="pointer" onClick={onClick}>
      {!isConstantLoad ? (
        <>
          <Arrow
            onClick={onClick}
            direction={directionStart}
            origin={loadPoint[0].to}
            length={rawDirectionStart.length()}
            label={startLabel}
            highlighted={isActive}
            color={renderColor}
            backgroundColor={backgroundColor}
            textXDirection={textXDirection}
            textYDirection={textYDirection}
            labelScale={loadTextScale}
            headWidth={LINE_LOAD_ARROW_HEAD_DIAMETER}
            lineWidth={LINE_LOAD_ARROW_SHAFT_DIAMETER}
            textOffsetHalfWidth={LINE_LOAD_TEXT_OFFSET_HALF_WIDTH}
            opacity={opacity}
          />
          <Arrow
            onClick={onClick}
            direction={directionEnd}
            origin={loadPoint[1].to}
            length={rawDirectionEnd.length()}
            label={endLabel}
            highlighted={isActive}
            color={renderColor}
            backgroundColor={backgroundColor}
            textXDirection={textXDirection}
            textYDirection={textYDirection}
            labelScale={loadTextScale}
            headWidth={LINE_LOAD_ARROW_HEAD_DIAMETER}
            lineWidth={LINE_LOAD_ARROW_SHAFT_DIAMETER}
            textOffsetHalfWidth={LINE_LOAD_TEXT_OFFSET_HALF_WIDTH}
            opacity={opacity}
          />
        </>
      ) : (
        <Text3DThreeDoubleSided
          textLines={toLoadSceneTextLines(endLabel)}
          color={renderColor}
          backgroundColor={backgroundColor}
          borderWidth={0.005}
          borderColor={renderColor}
          position={centerTo}
          anchorLocation="top"
          scale={loadTextScale}
          opacity={opacity}
          onClick={onClick ? () => onClick() : undefined}
          textXOrientation={textXDirection}
          textYOrientation={textYDirection}
          offsetBetweenTxtElementsHalfWidth={LINE_LOAD_TEXT_OFFSET_HALF_WIDTH}
        />
      )}
      <ShapeMesh
        data={shapeObject}
        applyThickness
        onClick={onClick}
        meshMaterialProps={{
          color: backgroundColor || theme.palette.grey[800],
          polygonOffset: true,
          polygonOffsetUnits: -offsetUnits,
          opacity: shapeOpacity,
          transparent: true,
          depthWrite: false,
        }}
      />
      {/* Tube geometry connecting start and end points */}
      <mesh material={tubeMaterial} onClick={onClick}>
        <tubeGeometry args={[lineCurve, 4, LINE_LOAD_TUBE_DIAMETER, 8, false]} />
      </mesh>
    </BasicMesh>
  )
}

export default LineLoadMeshElement
